import { Ban, CheckCircle2, RotateCcw, Target } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { GoalModal } from '../components/GoalModal';
import { goalsApi } from '../budgetGoalsApi';
import type { GoalListItem } from '../budgetGoalTypes';

type TabKey = 'info';

/** One goal, with the GOS triad the investment programme prints. */
export function GoalProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [goal, setGoal] = useState<GoalListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      goalsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setGoal(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this goal.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await goalsApi.activate(id);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this goal.', 0));
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Investment Planning',
    { label: 'Settings', to: '/procurement/investment-planning/settings' },
    { label: 'Goals', to: '/procurement/investment-planning/settings/goals' },
    goal?.name ?? 'Goal',
  ];

  if (error && !goal) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !goal) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="goal-profile"
        header={
          <ProfileHeader
            id="goal-profile-header"
            icon={<Target className="h-6 w-6" aria-hidden="true" />}
            title={goal.name}
            // The two rungs above it, joined by the chevron the product uses for a PATH
            // through a hierarchy. Not a comma — that is how an ADDRESS reads.
            meta={[`${goal.sectorName} › ${goal.subSectorName}`]}
            status={
              <StatusText tone={goal.isActive ? 'active' : 'inactive'}>
                {goal.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={goal.createdByName}
            createdAt={goal.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="goal-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : goal.isActive ? (
            <ProfileBanner
              id="goal-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This goal is offered: it appears in a budget plan's Goal Setting and in a PPA's alignment picker — provided its sub-sector and sector are both active too."
            />
          ) : (
            <ProfileBanner
              id="goal-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This goal is deleted. It is no longer offered, and the plans and PPAs already naming it keep resolving it."
              action={
                <PermissionGate permission="budget-goals.delete">
                  <Button
                    id="goal-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="goal-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'budget-goals.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Sector', value: goal.sectorName },
              {
                label: 'Sub-sector',
                value: (
                  <Link
                    to={`/procurement/investment-planning/settings/goal-sub-sectors/${goal.goalSubSectorId}`}
                    className="font-medium text-slate-800 hover:underline"
                  >
                    {goal.subSectorName}
                  </Link>
                ),
              },
              { label: 'Goal', value: goal.name },
              { label: 'Objective', value: goal.objective },
              /*
                Rendered whatever the value: an absent strategy draws an em dash rather than
                dropping the row. Dropping it would make the card a different shape per record,
                and a reader cannot tell a missing fact from one that does not apply.
              */
              { label: 'Strategy', value: goal.strategy },
              { label: 'Order', value: <span className="font-mono tabular-nums">{goal.sortOrder}</span> },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="goal-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: goal.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(goal.createdAt)} className="font-mono">
                    {formatDateTime(goal.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: goal.updatedByName },
              {
                label: 'Updated',
                value: goal.updatedAt ? (
                  <time dateTime={toIso(goal.updatedAt)} className="font-mono">
                    {formatDateTime(goal.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <GoalModal
          open
          editing={goal}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
